import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { fetchOptionsAnalytics } from '../lib/optionsApi';
import { buildCustomRangePayload, getDefaultCustomRange } from '../components/options/optionsDashboardUtils';
import { buildAnalyticsStrategyOptions } from '../components/options/optionsAnalyticsUtils';
import { useOptionsLocale } from '../context/OptionsLanguageContext';
import { useOptionsEntitlements } from '../context/OptionsEntitlementsContext';

export const useOptionsAnalyticsData = (connected) => {
  const { t } = useOptionsLocale();
  const { isPro, loading: entitlementsLoading, openUpgradeModal } = useOptionsEntitlements();
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState('usd');
  const [selectedRange, setSelectedRange] = useState('30d');
  const [customRange, setCustomRange] = useState(getDefaultCustomRange());
  const [appliedCustomRange, setAppliedCustomRange] = useState(getDefaultCustomRange());
  const [underlyingFilter, setUnderlyingFilter] = useState('all');
  const [strategyFilter, setStrategyFilter] = useState('all');

  const requestPayload = useMemo(() => {
    if (selectedRange !== 'custom') {
      return { range: selectedRange, start: null, end: null };
    }
    const { start, end } = buildCustomRangePayload(appliedCustomRange);
    return { range: selectedRange, start, end };
  }, [selectedRange, appliedCustomRange]);

  const strategyOptions = useMemo(
    () => analyticsData?.strategy_options?.length
      ? ['all', ...analyticsData.strategy_options]
      : buildAnalyticsStrategyOptions(analyticsData?.strategy_rows || []),
    [analyticsData]
  );

  const loadAll = useCallback(async () => {
    if (entitlementsLoading) return;
    if (!connected) {
      setLoading(false);
      setAnalyticsData(null);
      return;
    }

    setLoading(true);
    try {
      const data = await fetchOptionsAnalytics({
        ...requestPayload,
        currency: selectedCurrency,
        underlying: underlyingFilter,
        strategy: strategyFilter,
      });
      setAnalyticsData(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('toasts.failedLoadPerformance'));
    } finally {
      setLoading(false);
    }
  }, [connected, entitlementsLoading, requestPayload, selectedCurrency, underlyingFilter, strategyFilter, t]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (strategyFilter !== 'all' && !strategyOptions.includes(strategyFilter)) {
      setStrategyFilter('all');
    }
  }, [strategyFilter, strategyOptions]);

  const handleApplyCustomRange = useCallback(() => {
    if (!isPro) {
      openUpgradeModal('ANALYTICS');
      return;
    }
    if (!customRange.startDate || !customRange.endDate) {
      toast.error(t('toasts.selectBothDates'));
      return;
    }
    if (customRange.startDate > customRange.endDate) {
      toast.error(t('toasts.startBeforeEnd'));
      return;
    }
    setSelectedRange('custom');
    setAppliedCustomRange(customRange);
  }, [customRange, isPro, openUpgradeModal, t]);

  return {
    analyticsData,
    loading,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    underlyingFilter,
    setUnderlyingFilter,
    strategyFilter,
    setStrategyFilter,
    strategyOptions,
    handleApplyCustomRange,
  };
};

export default useOptionsAnalyticsData;